<template>
  <net-canvas
    ref="canvasElement"
    mode="view"
    :relations="relations"
    :targets="targets"
    :resources="resources"
    :indices="indices"
    :flows="flows"
    :flow-branches="flowBranches"
    :element-values="elementValues"
    :selection="selection"
    :impulses="impulses"
    :round-index="selectedRound?.index || 0"
    :fade-relations="areRelationsFaded"
    :fade-elements="elementsFaded"
    :show-selected-net-item-connections="showElementConnections"
    :net-transform="sceneTree.netTransform"
    @select-item="select"
    @add-select-item="addToSelection"
    @select-many="selectMany"
    @delete="deleteSelected"
    @clear-all-selection="deselectAll"
    @contextmenu="onContextmenu($event)"
    @update:net-transform="sceneTree.updateNetTransform"
    @update:canvas-dimensions="updateCanvasDimensions"
  >
    <net-sidebar-toggle-button />
  </net-canvas>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { flatMap } from "lodash-es";
import { useI18n } from "vue-i18n";

import NetCanvas from "@editor/net/NetCanvas.vue";
import NetSidebarToggleButton from "@editor/net/NetSidebarToggleButton.vue";

import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useOverlay } from "@editor/ui/overlayDialog.store";
import { useWayStore } from "@editor/items/way/ways.store";
import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useFlowStore } from "@editor/items/flow/flows.store";
import { useFlowBranchStore } from "@editor/items/flow/flowBranches.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useRightClickMenu } from "@core/ui/rightClickMenu.store";
import { useNetStore } from "@editor/net/net.store";
import { useIndexStore } from "@editor/items/index/indices.store";

import type { ContextmenuAction } from "@core/ui/rightClickMenu.store";
import type { CanvasDimensions } from "@editor/net/CanvasDimensions";
import type { ImpulseItem } from "@editor/items/impulse/ImpulseItem";
import type {
  CanvasSelection,
  ContextItemType,
  NetSelectableItemType
} from "@editor/net/NetCanvasProps";
import {
  normalizeIdentity,
  splitUid,
  toUid,
  type Uid
} from "@/helpers/utility/Id";
import { useSceneTree } from "@editor/items/scene/sceneTree";
import { isSceneElementNode } from "@editor/items/sceneElement/sceneElementFunctions";
import { notNull } from "@/helpers/utility/Type";
import { useSceneStore } from "@editor/items/scene/scenes.store";
import { usePresenceStore } from "@editor/store/presence.store";
import { useSceneNodesStore } from "@editor/items/scene/sceneNodes.store";

const canvasElement = ref<InstanceType<typeof NetCanvas>>();

const { t } = useI18n();

const simulationViewStore = useSimulationViewStore();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const indexStore = useIndexStore();
const flowStore = useFlowStore();
const flowBranchStore = useFlowBranchStore();
const relationStore = useRelationStore();
const inspectorViewStore = useInspectorView();
const wayStore = useWayStore();
const selectionStore = useSelectionStore();
const sceneTree = useSceneTree();
const sceneNodeStore = useSceneNodesStore();

const selectedRound = computed(() => simulationViewStore.selectedRound);

const relations = computed(() => relationStore.items);
const targets = computed(() => targetStore.items);
const resources = computed(() => resourceStore.items);
const indices = computed(() => indexStore.items);
const flows = computed(() => flowStore.items);
const flowBranches = computed(() => flowBranchStore.items);

const subview = computed(() => inspectorViewStore.subview);
const waysSubview = computed(() => inspectorViewStore.waysSubview);
const activeWay = computed(() => wayStore.active);

const activeWayItem = computed(() => {
  if (activeWay.value == null) return null;

  let itemId = null;
  let itemType = "target";

  if (activeWay.value.relationIds.length === 0) {
    if (activeWay.value.startItemId == null) return null;
    itemId = activeWay.value.startItemId;
    itemType = activeWay.value.startItemType;
  } else {
    const lastRelationId =
      activeWay.value.relationIds[activeWay.value.relationIds.length - 1];
    const lastRelation = relationStore.items[lastRelationId];
    itemId = lastRelation.endItemId;
    itemType = lastRelation.endItemType;
  }

  if (itemType == "target") {
    return targetStore.items[itemId];
  } else {
    return resourceStore.items[itemId];
  }
});

const selection = computed((): CanvasSelection => {
  const selection = {
    selected: selectionStore.selectedUids,
    highlighted: selectionStore.highlightedUids,
    active: new Set<string>()
  };

  if (subview.value == "matrix" || waysSubview.value == null) {
    return selection;
  }

  // if we are on the ways subview, show the connected way.
  if (waysSubview.value == "waysAndCycles") {
    if (wayBetweenSelectedItems.value) {
      const itemUids = wayBetweenSelectedItems.value.items.map((i) => i.uid);
      const relationUids = wayBetweenSelectedItems.value.relationIds.map((id) =>
        toUid("relation", id)
      );
      const nodes = itemUids
        .map((uid) => sceneTree.elementNodes.get(uid))
        .filter(notNull);
      const nodeUids = nodes.map((node) => node.uid);
      selection.selected = new Set([...itemUids, ...relationUids, ...nodeUids]);
    } else {
      const uids = new Set<Uid<"target" | "resource">>();
      if (wayStartItem.value !== null) {
        uids.add(wayStartItem.value.uid);
      }
      if (wayEndItem.value !== null) {
        uids.add(wayEndItem.value.uid);
      }
      const nodes = Array.from(uids)
        .map((uid) => sceneTree.elementNodes.get(uid))
        .filter(notNull);
      const nodeUids = nodes.map((node) => node.uid);

      const selectedUids = new Set<Uid<"target" | "resource" | "sceneNode">>(
        uids
      );
      for (const nodeUid of nodeUids) {
        selectedUids.add(nodeUid);
      }

      selection.selected = selectedUids;
    }
  } else if (waysSubview.value === "impulseTracking") {
    if (activeWayItem.value == null) return selection;

    const activeElementUid = activeWayItem.value.uid;
    selection.selected = new Set([activeElementUid]);

    const activeWayElementNode = sceneTree.elementNodes.get(activeElementUid);
    if (activeWayElementNode) {
      selection.selected.add(activeWayElementNode.uid);
    }

    activeWayRelationUids.value.forEach((uid) => selection.selected.add(uid));
    const highlightedElements = activeWayRelationItemUids.value;
    selection.highlighted = new Set([...highlightedElements]);
    for (const elementUid of highlightedElements) {
      const elementNode = sceneTree.elementNodes.get(elementUid);
      if (elementNode) {
        selection.highlighted.add(elementNode.uid);
      }
    }

    if (!activeWay.value?.complete) {
      // highlighted:
      const highlightedRelations = possibleNextRelationUids.value;
      for (const relationUid of highlightedRelations) {
        selection.highlighted.add(relationUid);
      }
    }
  }

  return selection;
});

const elementsFaded = computed(() => {
  if (subview.value == "ways" && waysSubview.value === "waysAndCycles") {
    return startAndEndSelected.value;
  } else if (subview.value == "matrix") {
    return true;
  } else {
    return false;
  }
});

const activeWayItems = computed(() => {
  if (activeWay.value == null) return [];

  const relations = useRelationStore().getArrayByIds(
    activeWay.value.relationIds
  );
  return flatMap(relations, (relation) => [
    normalizeIdentity({
      type: relation.startItemType,
      id: relation.startItemId
    }),
    normalizeIdentity({ type: relation.endItemType, id: relation.endItemId })
  ]);
});
const activeWayRelationItemUids = computed(
  (): Set<Uid<"target" | "resource" | "index">> => {
    if (activeWayItems.value.length == 0) return new Set();

    return new Set(activeWayItems.value.map((item) => item.uid));
  }
);

const activeWayRelationUids = computed((): Set<Uid<"relation">> => {
  const way = activeWay.value;
  if (way == null) return new Set<Uid<"relation">>();
  return new Set(way.relationIds.map((id) => toUid("relation", id)));
});

const possibleNextRelationUids = computed<Set<Uid<"relation">>>(() => {
  if (!activeWay.value) return new Set();
  if (activeWayItem.value == null) return new Set();

  const rels = relationStore
    .getByStartItem(activeWayItem.value)
    .filter((r) => r.updateType != "none");
  if (rels.length == 0) return new Set();
  return new Set(rels.map((rel) => rel.uid));
});

const elementValues = computed(() => {
  if (waysSubview.value == "impulseTracking" && activeWay.value) {
    const simulation = inspectorViewStore.itemStateChangeSimulation;
    const round = simulation.rounds[activeWay.value.relationIds.length];
    return round.elementValues;
  }

  return inspectorViewStore.elementValues;
});

const currentSimulation = computed(() => {
  return simulationViewStore.simulation;
});

const impulses = computed(() => {
  // are we in a simulation? show impulses
  if (subview.value == "matrix" && currentSimulation.value) {
    return simulationViewStore.openRoundImpulses;
  }
  return itemStateChangeImpulses.value;
});

/**
 * The impulses based on the changes to the values of the targets
 */
const itemStateChangeImpulses = computed(
  (): {
    [type in "target" | "resource"]: { [id: string]: ImpulseItem };
  } => {
    const impulses = inspectorViewStore.itemStateChangeSimulation.impulses;
    if (subview.value == "matrix") {
      return impulses[1];
    } else if (activeWay.value) {
      if (activeWay.value.complete) {
        const roundIndex = activeWay.value.relationIds.length;
        return impulses[roundIndex] || {};
      } else {
        const roundIndex = activeWay.value.relationIds.length + 1;
        return impulses[roundIndex] || {};
      }
    } else {
      return { target: {}, resource: {} };
    }
  }
);

const wayBetweenSelectedItems = computed(() => {
  return inspectorViewStore.wayBetweenSelectedItems;
});

const wayStartItem = computed(() => {
  const inspectorView = inspectorViewStore;
  const id = inspectorView.wayStartItemId;
  const type = inspectorView.wayStartItemType;
  if (id === null) return null;

  if (type == "target") {
    return targetStore.items[id] || null;
  } else {
    return resourceStore.items[id] || null;
  }
});

const wayEndItem = computed(() => {
  const inspectorView = inspectorViewStore;
  const id = inspectorView.wayEndItemId;
  const type = inspectorView.wayEndItemType;
  if (id === null) return null;

  if (type == "target") {
    return targetStore.items[id] || null;
  } else {
    return resourceStore.items[id] || null;
  }
});

const startAndEndSelected = computed(() => {
  return wayStartItem.value !== null && wayEndItem.value !== null;
});

const showElementConnections = computed(() => {
  if (subview.value === "ways" && waysSubview.value != null) return false;
  return true;
});

const areRelationsFaded = computed(() => {
  if (subview.value === "matrix") return false;
  return true;
});


watch(() => sceneTree.activeScene, (scene) => {
  if (scene == null) return;
  if (scene.editModeOnly) {
    console.log("active scene is editmode only, switching to default");
    const nonEditScene = useSceneStore().orderedItems.find(s => !s.editModeOnly);
    if (!nonEditScene) {
      console.warn("no non-edit scene found, staying on editmode only scene");
      return;
    }
    usePresenceStore().set("activeSceneId", nonEditScene.id);
  }
}, { immediate: true });

// METHODS

// NET INTERACTIONS
function select(item: { uid: Uid<NetSelectableItemType> }) {
  const identity = splitUid(item.uid);

  if (subview.value == "ways" && item) {
    let { type, id } = identity;
    if (identity.type == "sceneNode") {
      const sceneNode = sceneNodeStore.byUid[identity.uid];
      if (!sceneNode) return;
      if (isSceneElementNode(sceneNode)) {
        const elementIdentity = splitUid(sceneNode.data.elementUid);
        type = elementIdentity.type;
        id = elementIdentity.id;
      }
    }
    if (type != "target" && type != "resource") return;

    if (waysSubview.value === "waysAndCycles") {
      if (wayStartItem.value === null) {
        return inspectorViewStore.set({
          wayStartItemId: id,
          wayStartItemType: type
        });
      } else {
        return inspectorViewStore.set({
          wayEndItemId: id,
          wayEndItemType: type
        });
      }
    } else if (waysSubview.value === "impulseTracking") {
      if (activeWay.value) {
        // select target as start element if we have none now
        if (activeWay.value.startItemId == null) {
          return wayStore.setStartItem({
            id: activeWay.value.id,
            itemId: id,
            itemType: type
          });
        }
        // don't add anything if the way is complete
        else if (activeWay.value.complete) {
          return;
        }
        // deactivate element if already in queue
        else if (
          id == activeWayItem.value?.id &&
          type == activeWayItem.value?.type
        ) {
          return wayStore.removeLastRelation({
            id: activeWay.value.id
          });
        }
        // or add the element to the end of the line
        else {
          return wayStore.addItem({
            id: activeWay.value.id,
            itemId: id,
            itemType: type
          });
        }
      }
    }
  }

  if (selectionStore.contains(identity)) {
    if (identity.type == "sceneNode") {
      openItemInfo(identity);
      return;
    }
    if (subview.value === "matrix" || waysSubview.value == null) {
      openItemInfo(identity);
    } else {
      selectionStore.remove(identity);
    }
  } else {
    selectionStore.select(identity);
  }
}

function openItemInfo(item: { type: NetSelectableItemType; id: number }) {
  if (!item) return;
  if (item.type === "sceneNode") {
    const node = sceneNodeStore.byUid[toUid("sceneNode", item.id)];
    if (node && isSceneElementNode(node)) {
      const { type: elementType, id: elementId } = splitUid(node.data.elementUid);
      useOverlay().setItem({ type: elementType, id: elementId });
      return;
    }
    return;
  }
  useOverlay().setItem({ type: item.type, id: item.id });
}

function selectMany(items: { uid: Uid<NetSelectableItemType> }[]) {
  selectionStore.selectMany(items);
}

function addToSelection(item: { uid: Uid<NetSelectableItemType> }) {
  selectionStore.add(item);
}

function deleteSelected() {
  selectionStore.deleteSelected();
}

function deselectAll() {
  if (waysSubview.value === "waysAndCycles") {
    if (wayEndItem.value != null) {
      return inspectorViewStore.set({
        wayEndItemId: null
      });
    } else if (wayStartItem.value != null) {
      return inspectorViewStore.set({
        wayStartItemId: null
      });
    }
  }

  return selectionStore.clearAll();
}

function onContextmenu(
  event: MouseEvent & { item?: { uid: Uid<ContextItemType> } | null; items?: { uid: Uid<ContextItemType> }[] | null }
) {
  let actions: ContextmenuAction[] = [];

  let item = event.item;
  if (!item && event.items && event.items.length == 1) {
    item = event.items[0];
  }
  const itemUid = item?.uid || null;
  const { type: itemType, id: itemId } = itemUid
    ? splitUid(itemUid)
    : { type: null, id: null };

  if (item == null) {
    actions.push({
      title: t("actions.adjustView"),
      callback: () => {
        const net = canvasElement.value;
        if (net) {
          net.adjustView();
        }
      }
    });
  } else if (itemType && itemId != null) {
    if (
      !isOneOf(itemType, [
        "target",
        "resource",
        "index",
        "sceneNode",
        "relation",
        "momentum",
        "flow",
        "flowBranch"
      ] as const)
    ) {
      return;
    }
    actions.unshift({
      title: t("actions.showInformations"),
      callback: () => {
        openItemInfo({ type: itemType, id: itemId });
      }
    });
  }

  useRightClickMenu().show({
    x: event.clientX,
    y: event.clientY,
    actions: actions
  });
}

function updateCanvasDimensions(dimensions: CanvasDimensions) {
  useNetStore().canvasDimensions = dimensions;
}

function isOneOf<T>(value: any, options: T[]): value is T {
  return options.includes(value);
}
</script>

<style></style>
